import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getTasks, createTask, completeTask, failTask, deleteTask, getTaskStats, extractErrorMessage } from '../services/api';
import { FaPlus, FaCheck, FaTimes, FaTrash, FaEye } from 'react-icons/fa';
import { useToast } from '../components/Toast';
import '../styles/Tasks.css';

function Tasks({ user }) {
  const toast = useToast();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [newTask, setNewTask] = useState({
    user_id: '',
    title: '',
    description: '',
    category: 'light',
    points: 0,
    deadline: '',
    proof_required: false
  });

  useEffect(() => {
    fetchTasks();
    fetchStats();
  }, [filter]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const data = await getTasks({ status: filter });
      setTasks(data.tasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error(extractErrorMessage(error, 'Aufgaben konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await getTaskStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error(extractErrorMessage(error, 'Statistiken konnten nicht geladen werden'));
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormError(null);
    try {
      await createTask({
        ...newTask,
        deadline: newTask.deadline ? new Date(newTask.deadline).toISOString() : null
      });
      setNewTask({
        user_id: '',
        title: '',
        description: '',
        category: 'light',
        points: 0,
        deadline: '',
        proof_required: false
      });
      setShowModal(false);
      toast.success('Aufgabe erstellt');
      fetchTasks();
      fetchStats();
    } catch (error) {
      console.error('Error creating task:', error);
      setFormError(extractErrorMessage(error, 'Fehler beim Erstellen der Aufgabe'));
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (id) => {
    try {
      await completeTask(id);
      toast.success('Aufgabe erledigt');
      fetchTasks();
      fetchStats();
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Abschließen der Aufgabe'));
    }
  };

  const handleFail = async (id) => {
    const reason = prompt('Grund für das Fehlschlagen:');
    if (reason === null) return;
    try {
      await failTask(id, reason);
      toast.success('Aufgabe als fehlgeschlagen markiert');
      fetchTasks();
      fetchStats();
    } catch (error) {
      console.error('Error failing task:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Markieren der Aufgabe'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Aufgabe wirklich löschen?')) return;
    try {
      await deleteTask(id);
      toast.success('Aufgabe gelöscht');
      fetchTasks();
      fetchStats();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Löschen der Aufgabe'));
    }
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setFormError(null);
  };

  const getCategoryBadge = (category) => {
    const badges = {
      light: { class: 'light', label: 'Leicht' },
      medium: { class: 'medium', label: 'Mittel' },
      hard: { class: 'hard', label: 'Schwer' },
      hardcore: { class: 'hardcore', label: 'Hardcore' }
    };
    return badges[category] || badges.light;
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'pending', label: 'Offen' },
      completed: { class: 'completed', label: 'Erledigt' },
      failed: { class: 'failed', label: 'Fehlgeschlagen' }
    };
    return badges[status] || badges.pending;
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Aufgaben</h1>
          {user.isAdmin && (
            <button className="add-btn" onClick={() => setShowModal(true)}>
              <FaPlus /> Neue Aufgabe
            </button>
          )}
        </div>

        {stats && (
          <div className="stats-cards">
            <div className="stat-card">
              <span className="stat-value">{stats.total || 0}</span>
              <span className="stat-label">Gesamt</span>
            </div>
            <div className="stat-card completed">
              <span className="stat-value">{stats.completed || 0}</span>
              <span className="stat-label">Erledigt</span>
            </div>
            <div className="stat-card pending">
              <span className="stat-value">{stats.pending || 0}</span>
              <span className="stat-label">Offen</span>
            </div>
            <div className="stat-card failed">
              <span className="stat-value">{stats.failed || 0}</span>
              <span className="stat-label">Fehlgeschlagen</span>
            </div>
            <div className="stat-card points">
              <span className="stat-value">{stats.points_earned || 0}</span>
              <span className="stat-label">Punkte verdient</span>
            </div>
          </div>
        )}

        <div className="filter-tabs">
          <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>Alle</button>
          <button className={filter === 'pending' ? 'active' : ''} onClick={() => setFilter('pending')}>Offen</button>
          <button className={filter === 'completed' ? 'active' : ''} onClick={() => setFilter('completed')}>Erledigt</button>
          <button className={filter === 'failed' ? 'active' : ''} onClick={() => setFilter('failed')}>Fehlgeschlagen</button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="tasks-list">
            {tasks.length > 0 ? (
              tasks.map(task => {
                const catBadge = getCategoryBadge(task.category);
                const statusBadge = getStatusBadge(task.status);
                return (
                  <div key={task.id} className={`task-card ${task.status}`}>
                    <div className="task-header">
                      <span className={`category-badge ${catBadge.class}`}>{catBadge.label}</span>
                      <span className={`status-badge ${statusBadge.class}`}>{statusBadge.label}</span>
                    </div>
                    <h3 className="task-title">{task.title}</h3>
                    {task.description && <p className="task-desc">{task.description}</p>}
                    <div className="task-meta">
                      <span className="points">{task.points} Punkte</span>
                      {task.deadline && (
                        <span className="deadline">
                          Frist: {new Date(task.deadline).toLocaleDateString()}
                        </span>
                      )}
                      {task.proof_required === 1 && <span className="proof-badge">Beweis erforderlich</span>}
                    </div>
                    {task.status === 'pending' && (
                      <div className="task-actions">
                        <button className="action-btn complete" onClick={() => handleComplete(task.id)} title="Als erledigt markieren">
                          <FaCheck />
                        </button>
                        <button className="action-btn fail" onClick={() => handleFail(task.id)} title="Als fehlgeschlagen markieren">
                          <FaTimes />
                        </button>
                        {user.isAdmin && (
                          <button className="action-btn delete" onClick={() => handleDelete(task.id)} title="Löschen">
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="no-tasks">Keine Aufgaben gefunden</div>
            )}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neue Aufgabe</h2>
              {formError && <div className="shared-form-error-banner">{formError}</div>}
              <form onSubmit={handleCreateTask}>
                <div className="form-group">
                  <label>Benutzer-ID *</label>
                  <input
                    type="text"
                    value={newTask.user_id}
                    onChange={e => setNewTask({...newTask, user_id: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Titel *</label>
                  <input
                    type="text"
                    value={newTask.title}
                    onChange={e => setNewTask({...newTask, title: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Beschreibung</label>
                  <textarea
                    value={newTask.description}
                    onChange={e => setNewTask({...newTask, description: e.target.value})}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Kategorie</label>
                    <select
                      value={newTask.category}
                      onChange={e => setNewTask({...newTask, category: e.target.value})}
                    >
                      <option value="light">Leicht</option>
                      <option value="medium">Mittel</option>
                      <option value="hard">Schwer</option>
                      <option value="hardcore">Hardcore</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Punkte</label>
                    <input
                      type="number"
                      value={newTask.points}
                      onChange={e => setNewTask({...newTask, points: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Frist</label>
                  <input
                    type="datetime-local"
                    value={newTask.deadline}
                    onChange={e => setNewTask({...newTask, deadline: e.target.value})}
                  />
                </div>
                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={newTask.proof_required}
                      onChange={e => setNewTask({...newTask, proof_required: e.target.checked})}
                    />
                    Beweis erforderlich
                  </label>
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeModal} disabled={saving}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={saving}>{saving ? 'Speichert…' : 'Erstellen'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Tasks;
